import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useCallback, useMemo } from 'react';
import { useCancellableDelayedCallback } from '@elliemae/ds-utilities/hooks';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DSPopover, { PopperPositions } from '@elliemae/ds-popover';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var dsTestId = 'DS-SimpleTruncateText';

var isEllipsisActive = function isEllipsisActive(_ref) {
  var offsetWidth = _ref.offsetWidth,
      scrollWidth = _ref.scrollWidth;
  return offsetWidth < scrollWidth;
};

var initialTooltipState = function initialTooltipState() {
  var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return {
    reference: null,
    visible: false,
    value: value,
    options: options
  };
}; // reduce the possibility of error showing the tooltip (text-overflow: ellipsis) https://jira.elliemae.io/browse/PUI-1755


var Text = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-2f4vtq-0"
})(["text-overflow:ellipsis;white-space:nowrap;overflow:hidden;display:inline-block;max-width:100%;"]);

var SimpleTruncatedTooltipText = function SimpleTruncatedTooltipText(props) {
  var containerProps = props.containerProps,
      tooltipDelay = props.tooltipDelay,
      placement = props.placement,
      value = props.value,
      zIndex = props.zIndex,
      tooltipOptions = props.tooltipOptions,
      textOptions = props.textOptions; // not using "usePopoverProviderState" because usePopoverState has memory leak issues

  var _useState = useState(initialTooltipState(value, tooltipOptions)),
      _useState2 = _slicedToArray(_useState, 2),
      tooltipState = _useState2[0],
      setTooltipState = _useState2[1];

  var show = useCallback(function (newState) {
    setTooltipState(_objectSpread(_objectSpread(_objectSpread({}, tooltipState), newState), {}, {
      visible: true
    }));
  }, [tooltipState]);
  var hideTooltip = useCallback(function (newState) {
    setTooltipState(_objectSpread(_objectSpread(_objectSpread({}, tooltipState), newState), {}, {
      visible: false
    }));
  }, [tooltipState]);

  var _useCancellableDelaye = useCancellableDelayedCallback(show, tooltipDelay),
      _useCancellableDelaye2 = _slicedToArray(_useCancellableDelaye, 2),
      showTooltip = _useCancellableDelaye2[0],
      cancelShowTooltip = _useCancellableDelaye2[1];

  var handleMouseEnter = useCallback(function (e) {
    var target = e.target; // we search for the closest parent with data-testid matching this component
    //  this is required because the target may not be this component itself
    //  when the user gives JSX as a value.
    //  JSX as a value is required for features like text highlight during research
    //    wich would still allow the truncation behaviour (see tree view for example)
    // when the target has the test-id itself target===SimpleTruncatedTextEl

    var SimpleTruncatedTextEl = target.closest("[data-testid=\"".concat(dsTestId, "\"]"));

    if (SimpleTruncatedTextEl && isEllipsisActive(SimpleTruncatedTextEl)) {
      showTooltip({
        value: value,
        reference: SimpleTruncatedTextEl
      });
    }
  }, [showTooltip, value]);
  var handleMouseLeave = useCallback(function () {
    cancelShowTooltip();
    hideTooltip({
      reference: null
    });
  }, [hideTooltip, cancelShowTooltip]);
  var handlers = useMemo(function () {
    if (!showTooltip) return {};
    return {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    };
  }, [showTooltip, handleMouseEnter, handleMouseLeave]);
  var PurePopover = useMemo(function () {
    return /*#__PURE__*/React.createElement(React.Fragment, null, tooltipState.visible ? /*#__PURE__*/React.createElement(DSPopover, {
      boundaries: "window",
      style: {
        pointerEvents: 'none',
        zIndex: zIndex
      },
      placement: placement,
      content: tooltipState.value,
      referenceEl: tooltipState.reference,
      visible: tooltipState.visible,
      showArrow: true
    }) : null);
  }, [tooltipState, placement, tooltipOptions, zIndex]);
  var PureText = useMemo(function () {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, _extends({}, containerProps && _objectSpread({}, containerProps), textOptions && _objectSpread({}, textOptions), handlers && _objectSpread({}, handlers), {
      "data-testid": dsTestId // this is used by mouse enter too. required to support value as JSX

    }), value));
  }, [containerProps, textOptions, handlers]);
  var PureSimpleTruncatedTooltipText = useMemo(function () {
    return /*#__PURE__*/React.createElement(React.Fragment, null, PureText, PurePopover);
  }, [PureText, PurePopover]);
  return PureSimpleTruncatedTooltipText;
};

SimpleTruncatedTooltipText.propTypes = {
  containerProps: PropTypes.object,
  tooltipOptions: PropTypes.object,
  textOptions: PropTypes.object,

  /** Text that when truncated will trigger the tooltip interaction */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),

  /** Position of the tooltip */
  placement: PropTypes.oneOf([PopperPositions.AUTO_START, PopperPositions.AUTO_END, PopperPositions.AUTO, PopperPositions.TOP_START, PopperPositions.TOP, PopperPositions.TOP_END, PopperPositions.RIGHT_START, PopperPositions.RIGHT, PopperPositions.RIGHT_END, PopperPositions.BOTTOM_START, PopperPositions.BOTTOM, PopperPositions.BOTTOM_END, PopperPositions.LEFT_START, PopperPositions.LEFT, PopperPositions.LEFT_END]),

  /** Delay to show the tooltip */
  tooltipDelay: PropTypes.number,

  /** override default zIndex */
  zIndex: PropTypes.number
};
SimpleTruncatedTooltipText.defaultProps = {
  containerProps: {},
  tooltipOptions: {},
  textOptions: {},
  value: '',
  placement: PopperPositions.TOP,
  tooltipDelay: 200,
  zIndex: 110
};

export default SimpleTruncatedTooltipText;
export { SimpleTruncatedTooltipText };
//# sourceMappingURL=SimpleTruncatedTooltipText.js.map
